import { useState, type ReactNode } from 'react'

// Custom in-app replacements for the native browser confirm()/prompt() dialogs, which we never use.
// Both dim the app and follow the modal rule: dismissible only by an explicit action button
// (Cancel or the ✕), never by clicking the backdrop.

export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, confirmDisabled, onConfirm, onCancel }: {
    title: string
    message: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    danger?: boolean
    /**
     * Holds the confirm button shut while the dialog cannot yet answer for what it is about to do
     * — the message is still loading, or what it loaded rules the action out. Cancel and the ✕ are
     * never disabled: a dialog you cannot get out of is worse than one you cannot confirm.
     */
    confirmDisabled?: boolean
    onConfirm: () => void
    onCancel: () => void
}) {
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 440 }}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onCancel}>✕</button>
                </div>
                <div className="modal-body">
                    <div style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{message}</div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>{cancelLabel}</button>
                    <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} autoFocus disabled={confirmDisabled} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    )
}

export function PromptDialog({ title, label, defaultValue = '', placeholder, confirmLabel = 'Save', onConfirm, onCancel }: {
    title: string
    label?: string
    defaultValue?: string
    placeholder?: string
    confirmLabel?: string
    onConfirm: (value: string) => void
    onCancel: () => void
}) {
    const [value, setValue] = useState(defaultValue)
    const submit = () => { if (value.trim()) onConfirm(value.trim()) }
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 440 }}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onCancel}>✕</button>
                </div>
                <div className="modal-body">
                    <div className="form-field" style={{ margin: 0 }}>
                        {label && <label className="form-label">{label}</label>}
                        <input
                            className="form-input"
                            autoFocus
                            value={value}
                            placeholder={placeholder}
                            onChange={e => setValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') submit() }}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn btn-primary" disabled={!value.trim()} onClick={submit}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    )
}
